import React, { FC, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { shallowEqual, useSelector } from 'react-redux';
import { PageLink, PageTitle } from '../../../../rbts/layout/core';
import { KTSVG, SimpleAlert } from '../../../../rbts/helpers';
import { getSeedDemoStatusService, SeedDemoStatus } from '../../../service/seeddemo/SeedDemoService';
import { RootState } from '../../../../setup';

const breadCrumbs: Array<PageLink> = [];
const API_URL = process.env.REACT_APP_API;

const SeedDemoPage: FC = () => {
  const token = useSelector<RootState>(
    ({ auth }) => (auth as any).accessToken,
    shallowEqual
  ) as string;

  const [status, setStatus] = useState<SeedDemoStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [result, setResult] = useState<{ success: boolean; error?: string } | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  async function loadStatus() {
    setLoadingStatus(true);
    try {
      const data = await getSeedDemoStatusService();
      setStatus(data);
    } catch {
      SimpleAlert('Error al obtener el estado del sistema.', 3000, 'error');
    } finally {
      setLoadingStatus(false);
    }
  }

  async function handleRun() {
    setShowConfirm(false);
    setRunning(true);
    setLogs([]);
    setResult(null);

    try {
      const response = await fetch(`${API_URL}seedDemo/run`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok || !response.body) {
        throw new Error(`Error HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'log') {
              setLogs((prev) => [...prev, data.message]);
            } else if (data.type === 'done') {
              setResult({ success: data.success, error: data.error });
            }
          } catch {}
        }
      }
    } catch (err: any) {
      setResult({ success: false, error: err.message });
    } finally {
      setRunning(false);
      loadStatus();
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={breadCrumbs}>Datos de Demo</PageTitle>

      {/* Estado actual */}
      <div className="card mb-6">
        <div className="card-header border-0 pt-5">
          <h3 className="card-title fw-bold fs-4">Estado Actual del Sistema</h3>
          <div className="card-toolbar">
            <button
              className="btn btn-sm btn-light-primary"
              onClick={loadStatus}
              disabled={loadingStatus || running}
            >
              {loadingStatus ? (
                <span className="spinner-border spinner-border-sm me-1" />
              ) : (
                <KTSVG
                  path="/media/icons/duotune/arrows/arr029.svg"
                  className="svg-icon-3 me-1"
                />
              )}
              Actualizar
            </button>
          </div>
        </div>

        <div className="card-body py-4">
          {loadingStatus ? (
            <div className="d-flex justify-content-center py-4">
              <span className="spinner-border text-primary" />
            </div>
          ) : status?.tieneEventos ? (
            <>
              <div
                className="d-flex align-items-center p-5 mb-5 rounded"
                style={{ backgroundColor: '#fd7e14', color: '#fff' }}
              >
                <div
                  className="rounded-circle bg-white d-flex align-items-center justify-content-center me-4 flex-shrink-0"
                  style={{ width: 44, height: 44 }}
                >
                  <KTSVG
                    path="/media/icons/duotune/general/gen044.svg"
                    className="svg-icon-2hx svg-icon-warning"
                  />
                </div>
                <div>
                  <h5 className="mb-1 fw-bold text-white">El sistema tiene datos existentes</h5>
                  <span className="text-white">
                    Al ejecutar el reset se borrarán todos los datos transaccionales
                    y se recrearán los datos demo desde cero.
                  </span>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-row-bordered align-middle gs-0 gy-3">
                  <thead>
                    <tr className="fw-bold text-muted">
                      <th>ID</th>
                      <th>Nombre del Evento</th>
                      <th>Estado</th>
                      <th>Fecha Inicio</th>
                      <th>Fecha Fin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {status.eventos.map((ev) => (
                      <tr key={ev.id}>
                        <td className="text-muted">{ev.id}</td>
                        <td className="fw-bold">{ev.nombre}</td>
                        <td>
                          <span
                            className={`badge badge-light-${
                              ev.actual ? 'success' : 'secondary'
                            }`}
                          >
                            {ev.actual ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="text-muted">
                          {new Date(ev.fecha_inicio).toLocaleDateString('es-MX')}
                        </td>
                        <td className="text-muted">
                          {new Date(ev.fecha_fin).toLocaleDateString('es-MX')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="alert alert-info d-flex align-items-center p-5 mb-0">
              <KTSVG
                path="/media/icons/duotune/general/gen046.svg"
                className="svg-icon-2hx svg-icon-info me-4"
              />
              <div>
                <h5 className="mb-1 fw-bold">No hay datos de demo creados</h5>
                <span>
                  Se crearán todos los datos iniciales desde cero: evento, usuarios
                  operacionales, tipos de punto de venta, productos y más.
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="card-footer d-flex justify-content-end py-4">
          <button
            className="btn btn-danger"
            onClick={() => setShowConfirm(true)}
            disabled={running || loadingStatus}
          >
            <KTSVG
              path="/media/icons/duotune/general/gen019.svg"
              className="svg-icon-3 me-2"
            />
            {status?.tieneEventos ? 'Reiniciar datos de demo' : 'Crear datos de demo'}
          </button>
        </div>
      </div>

      {/* Log de ejecución */}
      {(running || logs.length > 0) && (
        <div className="card mb-6">
          <div className="card-header border-0 pt-5">
            <h3 className="card-title fw-bold fs-4 d-flex align-items-center">
              {running ? (
                <>
                  <span className="spinner-border spinner-border-sm text-primary me-3" />
                  Ejecutando seed...
                </>
              ) : result?.success ? (
                <>
                  <KTSVG
                    path="/media/icons/duotune/general/gen043.svg"
                    className="svg-icon-2 svg-icon-success me-2"
                  />
                  Proceso completado exitosamente
                </>
              ) : (
                <>
                  <KTSVG
                    path="/media/icons/duotune/general/gen040.svg"
                    className="svg-icon-2 svg-icon-danger me-2"
                  />
                  El proceso terminó con errores
                </>
              )}
            </h3>
          </div>
          <div className="card-body py-3">
            <div
              className="bg-dark rounded p-4"
              style={{
                maxHeight: '420px',
                overflowY: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.8rem',
              }}
            >
              {logs.map((line, i) => (
                <div key={i} className="text-light mb-1">
                  {line}
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>

            {result && !result.success && (
              <div className="alert alert-danger mt-4 mb-0">
                <strong>Error:</strong> {result.error}
              </div>
            )}
            {result?.success && (
              <div className="alert alert-success mt-4 mb-0">
                Los datos de demo se crearon correctamente.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de confirmación */}
      <Modal
        id="kt_modal_confirm_seed"
        tabIndex={-1}
        aria-hidden="true"
        dialogClassName="modal-dialog modal-dialog-centered mw-500px"
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h2>Confirmar acción</h2>
            <div
              className="btn btn-sm btn-icon btn-active-color-primary"
              onClick={() => setShowConfirm(false)}
            >
              <KTSVG
                className="svg-icon-2"
                path="/media/icons/duotune/arrows/arr061.svg"
              />
            </div>
          </div>
          <div className="modal-body mx-5">
            <div
              className="d-flex align-items-center p-5 mb-6 rounded"
              style={{ backgroundColor: '#fd7e14', color: '#fff' }}
            >
              <div
                className="rounded-circle bg-white d-flex align-items-center justify-content-center me-4 flex-shrink-0"
                style={{ width: 44, height: 44 }}
              >
                <KTSVG
                  path="/media/icons/duotune/general/gen044.svg"
                  className="svg-icon-2hx svg-icon-warning"
                />
              </div>
              <div>
                <h5 className="mb-1 fw-bold text-white">¿Estás seguro?</h5>
                <span className="text-white">
                  Esta acción <strong>borrará todos los datos transaccionales</strong>{' '}
                  (ventas, tickets, cajas, etc.) y recreará los datos demo desde cero.{' '}
                  <strong>Esta operación no se puede deshacer.</strong>
                </span>
              </div>
            </div>
            <div className="text-center pt-2 pb-3">
              <button
                type="button"
                className="btn btn-light me-3"
                onClick={() => setShowConfirm(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleRun}
              >
                Sí, reiniciar datos
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export { SeedDemoPage };
